import { Component, Prop, h } from '@stencil/core';
import { getConstant } from '../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * Call-to-action component for encouraging user engagement
 *
 * @product signnow
 * @version 1.0.2
 * @category SignNow
 * @contentConfig Call-to-action component for encouraging user engagement and conversions
 * @example <sn-call-to-action-type-3 constants='{"CALL_TO_ACTION_TYPE_2_HEADING":"Get legally-binding signatures now!","CALL_TO_ACTION_TYPE_2_DESCRIPTION":"be ready to get more","CALL_TO_ACTION_TYPE_2_PRIMARY_BUTTON":"Start free trial","LINK__CALL_TO_ACTION_TYPE_2_PRIMARY_BUTTON":"https://snseats.signnow.com/purchase/business_free_trial/loggedout","CALL_TO_ACTION_TYPE_2_SECONDARY_BUTTON":"Request a call","LINK__CALL_TO_ACTION_TYPE_2_SECONDARY_BUTTON":"/contact"}'></sn-call-to-action-type-3>
 * @constant CALL_TO_ACTION_TYPE_2_HEADING - "Get legally-binding signatures now!" - Main heading for call-to-action section
 * @constant CALL_TO_ACTION_TYPE_2_DESCRIPTION - "Sign up now and unlock your full potential with our platform." - Description text for call-to-action section
 * @constant CALL_TO_ACTION_TYPE_2_PRIMARY_BUTTON - "Start free trial" - Primary action button text
 * @constant LINK__CALL_TO_ACTION_TYPE_2_PRIMARY_BUTTON - "https://snseats.signnow.com/purchase/business_free_trial/loggedout" - Link for "Start free trial" button
 * @constant CALL_TO_ACTION_TYPE_2_SECONDARY_BUTTON - "Request a call" - Secondary action button text
 * @constant LINK__CALL_TO_ACTION_TYPE_2_SECONDARY_BUTTON - "/contact" - Link for "Request a call" button
 * @slot heading - {required}, <me-heading variant="2"> - Main CTA heading
 * @slot description - <me-text> - CTA description text
 * @slot action-primary - {required}, <me-button variant="primary"> - Primary action button
 * @slot action-secondary - <me-button variant="secondary"> - Secondary action button
 * @slot media - {required}, <me-picture> - Hero image for visual appeal
 */
@Component({
  tag: 'sn-call-to-action-type-3',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNCallToActionType3 {
  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * HTML tag for the heading element (h1, h2, h3, h4, h5, h6)
   * @default "h4"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h4';

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
  }

  render() {
    return (
      <call-to-action-type-2 class="sn-call-to-action-type-3">
        <me-text slot="heading" class="me-text--label">
          {getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_DESCRIPTION', 'be ready to get more')}
        </me-text>
        <me-heading slot="description" variant="2" tag={this.headingTag}>
          {getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_HEADING', 'Get legally-binding signatures now!')}
        </me-heading>
        <me-button
          slot="action-primary"
          variant="primary"
          href={getConstant(this.constants, 'LINK__CALL_TO_ACTION_TYPE_2_PRIMARY_BUTTON', 'https://snseats.signnow.com/purchase/business_free_trial/loggedout')}
        >
          {getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_PRIMARY_BUTTON', 'Start free trial')}
        </me-button>
        <me-button slot="action-secondary" variant="secondary" appearance="dark" href={getConstant(this.constants, 'LINK__CALL_TO_ACTION_TYPE_2_SECONDARY_BUTTON', '/contact')}>
          {getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_SECONDARY_BUTTON', 'Request a call')}
        </me-button>
        <div slot="media" class="page-section__column page-section__column--list">
          <me-picture
            slot="media"
            srcSet="https://marketing-static.signnow.com/329/images/e-signature/Illustration@2x.png 2x, https://marketing-static.signnow.com/329/images/e-signature/Illustration.png 1x"
            webpSrcSet="https://marketing-static.signnow.com/329/images/e-signature/Illustration@2x.webp 2x, https://marketing-static.signnow.com/329/images/e-signature/Illustration.webp 1x"
            aspectRatio="16/9"
            alt="Illustration"
          ></me-picture>
        </div>
      </call-to-action-type-2>
    );
  }
}
